/* eslint-disable @typescript-eslint/no-explicit-any */
import { goalbookUrls } from '@/util/links'
import { ErrorResponse } from '@/library/library_types'
export { setupHandleRequest, getHandleRequest, createHandleRequest, refreshJwtToken, getCookie, setCrossSiteLogin, handleRequest, parseJsonResponse, getCSRFHeaders, getOrStartRefresh }
export type { RequestContext, messageObj }

// Track pending refresh token request
let pendingRefreshPromise: Promise<Response> | null = null

interface messageObj {
  message: string
}

interface RequestContext<T = any> {
  requestFn: (...args: any[]) => Promise<Response | T> // Function to make the original request
  refreshFn: () => Promise<Response> // Function to refresh the JWT token
  loginFn: () => Promise<messageObj> // Function to handle reauthentication via login modal
  args: any[] // Arguments for the original request
}

// Module-level variable to store the request handler
let handleRequestInstance: ((...args: any[]) => Promise<Response | any>) | null = null

// Setup the request handler with a login function
function setupHandleRequest<T>(loginFunction?: () => Promise<messageObj>): ((...args: any[]) => Promise<Response | T>) | null {
  if (!loginFunction) {
    handleRequestInstance = null
    return null
  }
  handleRequestInstance = createHandleRequest(loginFunction)
  return handleRequestInstance as (...args: any[]) => Promise<Response | T>
}

// Get the current request handler
function getHandleRequest<T>(): ((...args: any[]) => Promise<Response | T>) {
  if (!handleRequestInstance) {
    throw new Error('Request handler not initialized')
  }
  return handleRequestInstance
}

function createHandleRequest(loginFn: () => Promise<messageObj>): ((...args: any[]) => Promise<Response | any>) {
  return async function({ requestFn, refreshFn = refreshJwtToken }, ...args) {
    const context: RequestContext = {
      requestFn,
      refreshFn,
      loginFn,
      args
    }
    return handleRequest(context)
  }
}

async function handleRequest<T>(context: RequestContext<T>): Promise<Response | T> {
  // Make the original request
  const response = await context.requestFn(...context.args)
  
  if (response instanceof Response) {
    // Credentials are missing or invalid. Refresh tokens or reauthenticate. 
    if (response.status === 401) {
      return handleAuthenticationError(context)
    }
    // For success, or other error status codes, parse and return the response. 
    return parseJsonResponse<T>(response)
  }
  
  // If response is already parsed JSON, return it
  return response as T
}

// Handle authentication errors by refreshing tokens or reauthenticating
async function handleAuthenticationError<T>(context: RequestContext<T>): Promise<Response | T> {
  // Ensure only a single refresh request is in-flight (single-flight)
  const refreshRes = await getOrStartRefresh(context.refreshFn)

  if (refreshRes.ok) {
    return handleRefreshTokenSuccess(context)
  }
  
  // If token refresh failed, try to reauthenticate
  try {
    const status = await context.loginFn()
    if (status.message === 'success') {
      return handleRequest<T>(context) // Retry original request
    } else {
      throw {
        status_code: 401,
        message: 'Failed to reauthenticate: login unsuccessful', 
        status: "error"
      } as ErrorResponse
    }
  } catch (e) {
    // If the error is already an ErrorResponse, rethrow it
    if ((e as any).status_code === 401 && (e as any).status === 'error') {
      throw e
    }
    // Otherwise wrap it in our error format
    throw {
      status_code: 401,
      message: 'Failed to reauthenticate: error with login',
      status: "error"
    } as ErrorResponse
  }
}

// Create or return the single in-flight refresh promise and clear it on completion
function getOrStartRefresh(refreshFn: () => Promise<Response>): Promise<Response> {
  if (!pendingRefreshPromise) {
    // Assign synchronously to avoid races before any awaits
    pendingRefreshPromise = (async () => {
      try {
        return await refreshFn()
      } finally {
        // Always clear, regardless of success or failure
        pendingRefreshPromise = null
      }
    })()
  }
  return pendingRefreshPromise
}

// If token refresh was successful, retry the original request
async function handleRefreshTokenSuccess<T>(context: RequestContext<T>): Promise<Response | T> {
  const response = await context.requestFn(...context.args)
  if (response instanceof Response) {
    return parseJsonResponse<T>(response)
  }
  return response as T
}

async function parseJsonResponse<T>(response: Response): Promise<Response | T> {
  let res 

  // Clone the response since we might need to read the body multiple times
  const clonedResponse = response.clone()

  try {
    res = await response.json()
  } catch {
    // If JSON parsing fails, get the text content or status text as fallback
    try {
      const text = await clonedResponse.text()
      res = { message: text || response.statusText || 'Unknown error' }
    } catch {
      res = { message: response.statusText || 'Unknown error' }
    }
  }

  if (response.status >= 400) {
    const error: ErrorResponse = { 
      status_code: response.status, 
      message: res.message || 'Unknown error', 
      status: "error",
      ...res  // Preserve all fields from the response body
    } as ErrorResponse
    throw error
  }

  return res as T
}

async function refreshJwtToken(): Promise<Response> {
  const response = await fetch(`${goalbookUrls.accounts}/auth/refresh`, {
    credentials: 'include',
    method: 'POST'
  })
  return response
}

async function setCrossSiteLogin(): Promise<Response> {
  const response = await fetch(`${goalbookUrls.accounts}/users/sign_in`, {
    credentials: 'include',
    method: 'GET'
  })
  return response
}

function getCookie(name: string): string {
  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(name + '='))
      ?.split('=')[1] || ''
  )
}

function getCSRFHeaders(): Headers {
  const gbcs = getCookie('gbcs')
  return new Headers({
    'Content-Type': 'application/json',
    'X-CSRF-Token': gbcs,
    'X-Requested-With': 'XMLHttpRequest'
  })
}
